import Link from "next/link";
import type {
  ComponentProps,
  ForwardRefRenderFunction,
  PropsWithChildren,
  ReactNode,
} from "react";
import classnames from "classnames";
import { Tooltip } from "@src/common/components";
import { NotificationCircle, COLOR } from "baseui/badge";
import React from "react";

export interface SidebarItemProps
  extends PropsWithChildren,
    Omit<ComponentProps<"div">, "text"> {
  active?: boolean;
  disabled?: boolean;
  tooltipContent?: string | undefined;
  icon?: any;
  label?: ReactNode;
  href?: string;
  badgeContent?: number;
}

const SidebarItem: ForwardRefRenderFunction<HTMLDivElement, SidebarItemProps> =
  (
    {
      label,
      icon,
      href,
      active = true,
      disabled = false,
      tooltipContent = undefined,
      onClick,
      badgeContent,
      className,
      ...rest
    },
    ref
  ) => {
    const content = (
      <div
        ref={ref}
        className={classnames(
          "rounded-xl flex flex-col p-1 justify-around items-center mx-1",
          {
            "cursor-not-allowed": disabled,
            "cursor-pointer": !disabled,
            "bg-primary-200 dark:bg-primary-900": active && !disabled,
            "hover:bg-slate-200 dark:hover:bg-gray-700": !disabled && !active,
            "bg-slate-100 dark:bg-gray-800": disabled,
          },
          className
        )}
        onClick={!disabled ? onClick : undefined}
        {...rest}
      >
        {badgeContent ? (
          <NotificationCircle content={badgeContent} color={COLOR.negative}>
            {tooltipContent ? (
              <Tooltip content={tooltipContent}>
                <div className={classnames({
                  "[&>svg]:text-primary-600 dark:[&>svg]:text-primary-400": active && !disabled,
                  "[&>svg]:text-slate-600 dark:[&>svg]:text-gray-300": !active || disabled,
                })}>
                  {icon || null}
                </div>
              </Tooltip>
            ) : (
              <div className={classnames({
                "[&>svg]:text-primary-600 dark:[&>svg]:text-primary-400": active && !disabled,
                "[&>svg]:text-slate-600 dark:[&>svg]:text-gray-300": !active || disabled,
              })}>
                {icon || null}
              </div>
            )}
          </NotificationCircle>
        ) : (
          <div className={classnames({
            "[&>svg]:text-primary-600 dark:[&>svg]:text-primary-400": active && !disabled,
            "[&>svg]:text-slate-600 dark:[&>svg]:text-gray-300": !active || disabled,
          })}>
            {tooltipContent ? (
              <Tooltip content={tooltipContent}>{icon || null}</Tooltip>
            ) : (
              icon || null
            )}
          </div>
        )}
        {label && (
          <span className="text-xs text-center text-slate-600 dark:text-gray-300">
            {label}
          </span>
        )}
      </div>
    );

    // Only wrap in Link if href is provided
    if (href) {
      return <Link href={href}>{content}</Link>;
    }

    return content;
  };

SidebarItem.displayName = "SidebarItem";

export default React.forwardRef(SidebarItem);
